/*
 * TallyQuizStore — IndexedDB-primary, sessionStorage fallback persistence
 * for Tally quiz submissions.
 *
 * API:
 *   TallyQuizStore.save({ formId, submissionId, fields, email })  -> Promise<void>
 *   TallyQuizStore.load(formId?)                                  -> Promise<record|null>
 *   TallyQuizStore.clear()                                        -> Promise<void>
 *   TallyQuizStore.setSubmissionCookie(fullId, days?)             -> void
 *
 * Record shape:
 *   { formId, submissionId, fields: Array, email: string, savedAt: number }
 *
 * Storage layout:
 *   IndexedDB DB "tally_quiz", object store "submissions", keyPath "formId".
 *   Plus a special key "__latest__" mirroring the most recently saved record so
 *   the results page can call load() with no arguments.
 *
 * sessionStorage fallback keys:
 *   tally_payload_data  -> JSON.stringify(fields)
 *   tally_user_email    -> email
 *   tally_form_id       -> formId
 *   tally_submission_id -> submissionId
 */
(function () {
  'use strict';

  var DB_NAME = 'tally_quiz';
  var DB_VERSION = 1;
  var STORE = 'submissions';
  var LATEST_KEY = '__latest__';

  var SS_FIELDS = 'tally_payload_data';
  var SS_EMAIL = 'tally_user_email';
  var SS_FORM = 'tally_form_id';
  var SS_SUB = 'tally_submission_id';

  var hasIDB = typeof indexedDB !== 'undefined';

  function openDB() {
    return new Promise(function (resolve, reject) {
      if (!hasIDB) {
        reject(new Error('IndexedDB unavailable'));
        return;
      }
      var req;
      try {
        req = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (err) {
        reject(err);
        return;
      }
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'formId' });
        }
      };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
      req.onblocked = function () { reject(new Error('IDB blocked')); };
    });
  }

  function txPromise(tx) {
    return new Promise(function (resolve, reject) {
      tx.oncomplete = function () { resolve(); };
      tx.onerror = function () { reject(tx.error); };
      tx.onabort = function () { reject(tx.error || new Error('IDB tx aborted')); };
    });
  }

  function reqPromise(request) {
    return new Promise(function (resolve, reject) {
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { reject(request.error); };
    });
  }

  function safeSession(fn) {
    try { return fn(); } catch (e) { return null; }
  }

  function fallbackSave(record) {
    safeSession(function () {
      sessionStorage.setItem(SS_FIELDS, JSON.stringify(record.fields || []));
      if (record.email) sessionStorage.setItem(SS_EMAIL, record.email);
      if (record.formId) sessionStorage.setItem(SS_FORM, record.formId);
      if (record.submissionId) sessionStorage.setItem(SS_SUB, record.submissionId);
    });
  }

  function fallbackLoad() {
    return safeSession(function () {
      var raw = sessionStorage.getItem(SS_FIELDS);
      if (!raw) return null;
      var fields;
      try { fields = JSON.parse(raw); } catch (e) { return null; }
      if (!Array.isArray(fields)) return null;
      return {
        formId: sessionStorage.getItem(SS_FORM) || '',
        submissionId: sessionStorage.getItem(SS_SUB) || '',
        fields: fields,
        email: sessionStorage.getItem(SS_EMAIL) || '',
        savedAt: Date.now()
      };
    });
  }

  function fallbackClear() {
    safeSession(function () {
      sessionStorage.removeItem(SS_FIELDS);
      sessionStorage.removeItem(SS_EMAIL);
      sessionStorage.removeItem(SS_FORM);
      sessionStorage.removeItem(SS_SUB);
    });
  }

  function save(input) {
    var record = {
      formId: (input && input.formId) || '',
      submissionId: (input && input.submissionId) || '',
      fields: (input && input.fields) || [],
      email: (input && input.email) || '',
      savedAt: Date.now()
    };

    // Always mirror to sessionStorage so the results page works even if the
    // user's browser blocks IDB writes silently.
    fallbackSave(record);

    if (!hasIDB) return Promise.resolve();

    return openDB().then(function (db) {
      var tx = db.transaction(STORE, 'readwrite');
      var store = tx.objectStore(STORE);
      // Per-form record (keyed by formId)
      if (record.formId) store.put(record);
      // Latest pointer record
      var latest = Object.assign({}, record, { formId: LATEST_KEY, originalFormId: record.formId });
      store.put(latest);
      return txPromise(tx).then(function () { db.close(); });
    }).catch(function () {
      // already mirrored to sessionStorage
    });
  }

  function load(formId) {
    var key = formId || LATEST_KEY;
    if (!hasIDB) return Promise.resolve(fallbackLoad());

    return openDB().then(function (db) {
      var tx = db.transaction(STORE, 'readonly');
      var store = tx.objectStore(STORE);
      return reqPromise(store.get(key)).then(function (rec) {
        db.close();
        if (rec && rec.formId === LATEST_KEY) {
          rec = Object.assign({}, rec, { formId: rec.originalFormId || '' });
          delete rec.originalFormId;
        }
        return rec || fallbackLoad();
      });
    }).catch(function () {
      return fallbackLoad();
    });
  }

  function clear() {
    fallbackClear();
    if (!hasIDB) return Promise.resolve();
    return openDB().then(function (db) {
      var tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      return txPromise(tx).then(function () { db.close(); });
    }).catch(function () { /* swallow */ });
  }

  function setSubmissionCookie(fullId, days) {
    if (!fullId) return;
    var maxAge = (days || 30) * 24 * 60 * 60;
    document.cookie = 'tally_submission_id=' + encodeURIComponent(fullId)
      + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
  }

  window.TallyQuizStore = {
    save: save,
    load: load,
    clear: clear,
    setSubmissionCookie: setSubmissionCookie
  };
})();
